import React, { useState } from "react";
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Button } from "@material-ui/core";

// Define CustomWidthTooltip
const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "15rem",
    fontSize: "1rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
  },
});

// Helper function to convert RGB/RGBA to hex
function rgbToHex(rgb) {
  if (!rgb || rgb === 'transparent' || rgb === 'rgba(0, 0, 0, 0)' || rgb === 'rgba(0,0,0,0)') {
    return undefined;
  }

  // Handle rgb/rgba format
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (match) {
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);

    // Validate RGB values are in valid range
    if (isNaN(r) || isNaN(g) || isNaN(b) || r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
      return undefined;
    }

    const rHex = r.toString(16).padStart(2, '0').toUpperCase();
    const gHex = g.toString(16).padStart(2, '0').toUpperCase();
    const bHex = b.toString(16).padStart(2, '0').toUpperCase();
    return rHex + gHex + bHex;
  }

  // If already hex format, remove # if present and validate
  if (rgb.startsWith('#')) {
    const hex = rgb.substring(1).toUpperCase();
    if (/^[0-9A-F]{6}$/.test(hex)) {
      return hex;
    }
  }

  return undefined;
}

// Helper function to convert cm to inches
const cmToInches = (cm) => cm / 2.54;

// Title style for slides
// Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
// With 0.5cm margins, usable width = 24.4cm
const titleStyle = {
  x: cmToInches(0.5),
  y: cmToInches(0.8),
  w: cmToInches(24.4),
  h: cmToInches(1.5),
  fontSize: 18,
  fontFace: 'Montserrat',
  bold: true,
  align: 'left',
  valign: 'middle',
};

// Azure Blob Storage base URL for PPTX slide images
const BLOB_STORAGE_BASE_URL = 'https://skygeniwebapp.blob.core.windows.net/pptx-slides';

// Cache for loaded images to avoid reloading for each slide
let masterSlideImagesCache = null;

// Helper function to load image as base64 with dimensions
function loadImageAsBase64(imagePath) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        // Always convert to PNG for better compatibility with PptxGenJS
        const dataUrl = canvas.toDataURL('image/png');
        if (!dataUrl || dataUrl === 'data:,') {
          throw new Error('Failed to convert image to base64');
        }
        resolve({ dataUrl, width: img.width, height: img.height });
      } catch (error) {
        console.error(`Error converting image ${imagePath} to base64:`, error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error(`Error loading image ${imagePath}:`, error);
      reject(new Error(`Failed to load image: ${imagePath}`));
    };

    // Set src after event handlers are attached
    img.src = imagePath;
  });
}

// Function to load master slide images (called once)
async function loadMasterSlideImages() {
  if (masterSlideImagesCache) {
    return masterSlideImagesCache;
  }

  try {
    const results = await Promise.allSettled([
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Top Right.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Bottom Left.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/logo@3x.webp`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/Top Right line.png`),
      loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/powerdby.png`)
    ]);

    const topRightImage = results[0].status === 'fulfilled' ? results[0].value.dataUrl : null;
    const bottomLeftImage = results[1].status === 'fulfilled' ? results[1].value.dataUrl : null;
    const logoImage = results[2].status === 'fulfilled' ? results[2].value.dataUrl : null;
    const topRightLineResult = results[3].status === 'fulfilled' ? results[3].value : null;
    const topRightLineImage = topRightLineResult ? topRightLineResult.dataUrl : null;
    const topRightLineDimensions = topRightLineResult ? { width: topRightLineResult.width, height: topRightLineResult.height } : null;
    const powerdbyResult = results[4].status === 'fulfilled' ? results[4].value : null;
    const powerdbyImage = powerdbyResult ? powerdbyResult.dataUrl : null;
    const powerdbyDimensions = powerdbyResult ? { width: powerdbyResult.width, height: powerdbyResult.height } : null;

    masterSlideImagesCache = {
      topRightImage,
      bottomLeftImage,
      logoImage,
      topRightLineImage,
      topRightLineDimensions,
      powerdbyImage,
      powerdbyDimensions
    };
    return masterSlideImagesCache;
  } catch (error) {
    console.error('Error loading master slide images:', error);
    masterSlideImagesCache = {
      topRightImage: null,
      bottomLeftImage: null,
      logoImage: null,
      topRightLineImage: null,
      topRightLineDimensions: null,
      powerdbyImage: null,
      powerdbyDimensions: null
    };
    return masterSlideImagesCache;
  }
}

function RegionalSalesReviewPackagePPTX({
  dashboardTitle = "Regional Sales Review Package",
  fileName = "Regional_Sales_Review_Package",
  currentQuarter,
  currentWeekNumber,
  inline = false // if true, renders as inline icon button; if false, renders as fixed button
}) {
  const [tooltipText, setTooltipText] = useState("Download as PowerPoint");
  const [loading, setLoading] = useState(false);


  // Check if element contains a table
  const containsTable = (element) => {
    const table = element.querySelector('table');
    return table !== null && table.offsetParent !== null;
  };

  // Extract table data from HTML table element with full styling (from GlobalDownloadButton.js)
  const extractTableData = (tableElement) => {
    if (!tableElement) return null;

    const tableRows = Array.from(tableElement.querySelectorAll('tr'));
    const tableData = [];

    // Process each row
    tableRows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];

      // Get row-level styling
      const rowComputedStyle = window.getComputedStyle(row);
      const rowBgColor = rowComputedStyle.backgroundColor;

      cells.forEach((cell) => {
        const cellText = cell.textContent.trim().replace(/\s+/g, ' ');
        // Get colspan
        const colSpanAttr = cell.getAttribute('colspan') || cell.getAttribute('colSpan') || cell.colSpan || '1';
        const colSpan = parseInt(colSpanAttr) || 1;

        // Get cell styling
        const computedStyle = window.getComputedStyle(cell);
        const bgColor = computedStyle.backgroundColor;
        const textColor = computedStyle.color;
        const fontWeight = computedStyle.fontWeight;
        const fontStyle = computedStyle.fontStyle;
        const textAlign = computedStyle.textAlign || 'left';
        const verticalAlign = computedStyle.verticalAlign || 'middle';

        // Build cell object
        const cellObj = {
          text: String(cellText || '').replace(/\0/g, ''),
        };

        const options = {};

        // Background color - use cell color, fallback to row color
        let finalBgColor = bgColor;
        if ((!bgColor || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') &&
            rowBgColor && rowBgColor !== 'rgba(0, 0, 0, 0)' && rowBgColor !== 'transparent') {
          finalBgColor = rowBgColor;
        }
        const hexBg = rgbToHex(finalBgColor);
        if (hexBg) {
          options.fill = hexBg;
        }

        // Text color
        const hexText = rgbToHex(textColor);
        if (hexText) {
          options.color = hexText;
        }

        // Font weight (Bold)
        const isBold = fontWeight === 'bold' || parseInt(fontWeight) >= 600;
        if (isBold) {
          options.bold = true;
        }

        // Font style (Italic)
        if (fontStyle === 'italic') {
          options.italic = true;
        }

        // Font size - fixed at 6pt for all table cells
        options.fontSize = 6;

        // Font face - Roboto for all table cells
        options.fontFace = 'Roboto';

        // Horizontal alignment
        if (textAlign === 'center') {
          options.align = 'center';
        } else if (textAlign === 'right') {
          options.align = 'right';
        } else {
          options.align = 'left';
        }

        // Vertical alignment
        if (verticalAlign === 'top') {
          options.valign = 'top';
        } else if (verticalAlign === 'bottom') {
          options.valign = 'bottom';
        } else {
          options.valign = 'middle';
        }

        // Colspan
        if (colSpan > 1 && colSpan <= 100) {
          options.colspan = colSpan;
        }

        cellObj.options = options;
        rowData.push(cellObj);
      });

      if (rowData.length > 0) {
        tableData.push(rowData);
      }
    });

    return tableData;
  };

  // Add table to slide with chunking (from GlobalDownloadButton.js)
  const addTableToSlide = (pptx, firstSlide, element, slideTitle) => {
    try {
      // Find all tables in the element
      const tables = Array.from(element.querySelectorAll('table'));
      const visibleTables = tables.filter(table => table.offsetParent !== null);

      if (visibleTables.length === 0) {
        console.warn('No visible tables found in element');
        return;
      }

      // Track if firstSlide has been used (for multiple tables in same Paper)
      let firstSlideUsed = false;
      let tableIndex = 0;

      // Process each table (may be multiple per Paper - e.g., summary + main table)
      for (const tableElement of visibleTables) {
        const tableData = extractTableData(tableElement);
        if (!tableData || tableData.length === 0) continue;

        // Try to find a specific title for this table by looking for preceding Typography
        // For tables, show only the table title, not the paper title
        let tableSpecificTitle = slideTitle;
        const tableParent = tableElement.closest('[class*="MuiBox-root"]') || tableElement.parentElement;
        if (tableParent) {
          const precedingSibling = tableParent.previousElementSibling;
          if (precedingSibling) {
            const typographyEl = precedingSibling.querySelector('[class*="MuiTypography-"]') ||
                                 (precedingSibling.classList.contains('MuiTypography-root') ? precedingSibling : null);
            if (typographyEl) {
              const subTitle = typographyEl.textContent.trim();
              if (subTitle && subTitle.length > 2 && subTitle.length < 100) {
                // Use only the table title, not the paper title
                tableSpecificTitle = subTitle;
              }
            }
          }
        }
        tableIndex++;

        const tableRows = Array.from(tableElement.querySelectorAll('tr'));
        const tableComputedStyle = window.getComputedStyle(tableElement);

        // Extract border info
        let tableBorderColor = null;
        let tableBorderWidth = 1;

        // Try to get border from computed style
        const borderColor = tableComputedStyle.borderColor || tableComputedStyle.borderTopColor;
        if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && borderColor !== 'transparent') {
          tableBorderColor = rgbToHex(borderColor);
        }
        const borderWidth = parseFloat(tableComputedStyle.borderWidth || tableComputedStyle.borderTopWidth);
        if (borderWidth && !isNaN(borderWidth) && borderWidth > 0) {
          tableBorderWidth = Math.max(1, Math.round(borderWidth));
        }

        // Extract column widths from first row
        const originalColWidths = [];
        let totalOriginalWidth = 0;
        const firstRow = tableRows[0];

        if (firstRow) {
          const cells = Array.from(firstRow.querySelectorAll('th, td'));
          cells.forEach((cell) => {
            const computedStyle = window.getComputedStyle(cell);
            const width = parseFloat(computedStyle.width) || cell.offsetWidth;
            const colSpan = parseInt(cell.getAttribute('colspan') || '1');

            const cellWidth = width / colSpan;
            for (let i = 0; i < colSpan; i++) {
              originalColWidths.push(cellWidth);
              totalOriginalWidth += cellWidth;
            }
          });
        }

        // Calculate max columns
        const maxCols = Math.max(...tableData.map(row => {
          let cols = 0;
          row.forEach(cell => {
            cols += (cell.options?.colspan || 1);
          });
          return cols;
        }));

        // Calculate column widths (scaled to fit slide)
        // Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
        // With 0.5cm margins on each side, usable width = 25.4 - 1.0 = 24.4cm
        let colWidths;
        const usableWidthCm = 24.4;
        if (originalColWidths.length === maxCols && totalOriginalWidth > 0) {
          const scaleFactor = usableWidthCm / (totalOriginalWidth / 96 * 2.54);
          colWidths = originalColWidths.map(width => cmToInches((width / 96 * 2.54) * scaleFactor));
        } else {
          const colWidth = cmToInches(usableWidthCm / maxCols);
          colWidths = Array(maxCols).fill(colWidth);
        }

        // Detect header rows
        const headerRows = [];
        let headerRowCount = 0;

        for (let i = 0; i < Math.min(2, tableRows.length); i++) {
          const row = tableRows[i];
          const hasTh = row.querySelector('th') !== null;
          const isBold = tableData[i] && tableData[i].some(cell => cell.options?.bold);

          if (hasTh || isBold) {
            headerRows.push(tableData[i]);
            headerRowCount++;
          } else {
            break;
          }
        }

        // Data rows
        const dataRows = tableData.slice(headerRowCount);

        // Calculate rows per slide
        // Use more conservative row height estimates to prevent overflow
        const baseDataRowHeight = 0.6;
        const rowHeightMultiplier = maxCols > 10 ? 1.3 : maxCols > 6 ? 1.2 : 1.1;
        const adjustedDataRowHeight = baseDataRowHeight * rowHeightMultiplier;
        const adjustedHeaderRowHeight = 0.85 * rowHeightMultiplier;

        // Default LAYOUT_16x9 height is 14.29cm
        // Title area: 0.8 to 2.3cm (table starts at y = 2.4cm)
        // Footer area starts at ~13.0cm (logo, copyright, powered by)
        // So usable height for table: 12.8 - 2.4 = 10.4cm (with some safety margin)
        const tableStartY = 2.4;
        const maxTableBottomY = 12.8; // Leave space for footer elements
        const availableHeight = maxTableBottomY - tableStartY; // ~11.0cm
        const headerHeight = headerRows.length * adjustedHeaderRowHeight;
        const dataRowsHeight = availableHeight - headerHeight;
        // Use 0.85 multiplier for additional safety margin
        const maxRowsPerSlide = Math.max(1, Math.floor((dataRowsHeight * 0.85) / adjustedDataRowHeight));

        // Split into chunks
        const chunks = [];
        if (dataRows.length > 0) {
          for (let i = 0; i < dataRows.length; i += maxRowsPerSlide) {
            chunks.push(dataRows.slice(i, i + maxRowsPerSlide));
          }
        } else if (headerRows.length > 0) {
          chunks.push([]);
        }

        // Create slides for each chunk
        chunks.forEach((chunk, chunkIndex) => {
          // Use firstSlide only for the very first chunk of the first table
          let slide;
          if (chunkIndex === 0 && !firstSlideUsed) {
            slide = firstSlide;
            firstSlideUsed = true;
          } else {
            slide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
          }

          // Add title (with page number for subsequent slides)
          const titleText = chunks.length > 1 && chunkIndex > 0
            ? `${tableSpecificTitle} (Page ${chunkIndex + 1} of ${chunks.length})`
            : tableSpecificTitle;

          slide.addText(titleText, titleStyle);

          // Prepare table data with header + chunk
          const slideTableData = [...headerRows];

          // Set font size to 6 and font face to Roboto for data rows
          const chunkWithFontSize = chunk.map(row => {
            return row.map(cell => {
              const cellCopy = { ...cell };
              if (!cellCopy.options) cellCopy.options = {};
              cellCopy.options.fontSize = 6;
              cellCopy.options.fontFace = 'Roboto';
              return cellCopy;
            });
          });
          slideTableData.push(...chunkWithFontSize);

          if (slideTableData.length > 0) {
            const totalScaledWidth = colWidths.reduce((sum, width) => sum + width, 0);

            const validColWidths = colWidths.map(w => {
              if (typeof w !== 'number' || isNaN(w) || !isFinite(w) || w <= 0) {
                return cmToInches(1.0);
              }
              return Math.max(0.1, w);
            });

            const validTotalWidth = Math.max(0.1, totalScaledWidth);

            const tableOptions = {
              x: cmToInches(0.5),
              y: cmToInches(2.4),
              w: validTotalWidth,
              colW: validColWidths,
              fontFace: 'Roboto',
              fontSize: 6,
            };

            // Set border
            if (tableBorderColor && /^[0-9A-Fa-f]{6}$/i.test(tableBorderColor)) {
              tableOptions.border = {
                type: "solid",
                color: tableBorderColor.toUpperCase(),
                pt: Math.max(1, Math.min(10, tableBorderWidth))
              };
            } else {
              tableOptions.border = {
                type: "solid",
                color: "CCCCCC",
                pt: 1
              };
            }

            try {
              slide.addTable(slideTableData, tableOptions);
            } catch (error) {
              console.error('Error adding table:', error);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error in addTableToSlide:', error);
    }
  };

  // Add chart as image to slide
  const addChartImageToSlide = async (slide, element, slideTitle) => {
    try {
      // Find and temporarily hide title elements before capture
      const titleElements = [];

      // Find Typography h5 and h6 titles (common title patterns)
      const h5Titles = element.querySelectorAll('[class*="MuiTypography-h5"]');
      const h6Titles = element.querySelectorAll('[class*="MuiTypography-h6"]');

      // Collect all title elements and store their original display values
      [...h5Titles, ...h6Titles].forEach(el => {
        titleElements.push({
          element: el,
          originalDisplay: el.style.display
        });
        // Hide the title element completely
        el.style.display = 'none';
      });

      // Capture as image (without titles)
      const canvas = await html2canvas(element, {
        backgroundColor: "#FFFFFF",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      // Restore title elements after capture
      titleElements.forEach(({ element: el, originalDisplay }) => {
        el.style.display = originalDisplay || '';
      });

      const imageBase64 = canvas.toDataURL();
      const imageWidth = canvas.width;
      const imageHeight = canvas.height;

      // Add title
      slide.addText(slideTitle, titleStyle);

      // Calculate dimensions
      // Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
      const slideWidthCm = 25.4;
      const maxWidthCm = 24.4; // usable width with margins
      const maxHeightCm = 10.5;
      const imageStartY = 2.4;

      const imageAspectRatio = imageWidth / imageHeight;
      const maxAspectRatio = maxWidthCm / maxHeightCm;

      let adjustedWidthCm;
      let adjustedHeightCm;

      if (imageAspectRatio > maxAspectRatio) {
        adjustedWidthCm = maxWidthCm;
        adjustedHeightCm = adjustedWidthCm / imageAspectRatio;
      } else {
        adjustedHeightCm = maxHeightCm;
        adjustedWidthCm = adjustedHeightCm * imageAspectRatio;
      }

      // Center the image horizontally on the slide
      const adjustedXPos = (slideWidthCm - adjustedWidthCm) / 2;

      slide.addImage({
        data: imageBase64,
        x: cmToInches(adjustedXPos),
        y: cmToInches(imageStartY),
        w: cmToInches(adjustedWidthCm),
        h: cmToInches(adjustedHeightCm),
      });
    } catch (error) {
      console.error('Error adding chart image:', error);
    }
  };

  // Get slide title from Paper element
  const getSlideTitle = (paperElement) => {
    // Try to find Typography with h5 or h6 variant for title
    const h5Title = paperElement.querySelector('[class*="MuiTypography-h5"]');
    if (h5Title) {
      return h5Title.textContent.trim();
    }

    const h6Title = paperElement.querySelector('[class*="MuiTypography-h6"]');
    if (h6Title) {
      return h6Title.textContent.trim();
    }

    // Fallback: try to find any Typography element that looks like a title
    const typographyElements = paperElement.querySelectorAll('[class*="MuiTypography-"]');
    for (const el of typographyElements) {
      const text = el.textContent.trim();
      if (text && text.length > 10 && text.length < 150) {
        return text;
      }
    }

    return null;
  };

  const handleExport = async () => {
    setLoading(true);
    setTooltipText("Exporting all sections...");

    try {
      // Create the PowerPoint presentation
      const pptx = new PptxGenJS();

      // Use default layout LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
      // Don't override - use PptxGenJS default

      // Load master slide images
      const masterImages = await loadMasterSlideImages();

      // Build master slide objects array
      // Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
      const slideWidthCm = 25.4;
      const slideHeightCm = 14.29;
      const masterObjects = [];

      // Top Horizontal Line (using Top Right line.png image)
      if (masterImages.topRightLineImage && masterImages.topRightLineDimensions) {
        const desiredWidthCm = 16.00;
        const maxHeightCm = 0.5;
        const imageAspectRatio = masterImages.topRightLineDimensions.width / masterImages.topRightLineDimensions.height;
        let finalWidthCm = desiredWidthCm;
        let finalHeightCm = desiredWidthCm / imageAspectRatio;
        if (finalHeightCm > maxHeightCm) {
          finalHeightCm = maxHeightCm;
          finalWidthCm = maxHeightCm * imageAspectRatio;
        }
        masterObjects.push({
          image: {
            data: masterImages.topRightLineImage,
            x: cmToInches(0.4),
            y: cmToInches(0.3),
            w: cmToInches(finalWidthCm),
            h: cmToInches(finalHeightCm),
          }
        });
      }

      // Top Right Pattern Image
      if (masterImages.topRightImage) {
        masterObjects.push({
          image: {
            data: masterImages.topRightImage,
            x: cmToInches(19.4), // (25.4 - 6.0)
            y: 0.0,
            w: cmToInches(6.0),
            h: cmToInches(5.0),
          }
        });
      }

      // Bottom Left Pattern Image
      if (masterImages.bottomLeftImage) {
        masterObjects.push({
          image: {
            data: masterImages.bottomLeftImage,
            x: 0.0,
            y: cmToInches(9.29), // (14.29 - 5.0)
            w: cmToInches(5.0),
            h: cmToInches(5.0),
          }
        });
      }

      // Bottom Left Logo
      if (masterImages.logoImage) {
        masterObjects.push({
          image: {
            data: masterImages.logoImage,
            x: cmToInches(0.4),
            y: cmToInches(13.70), // Near bottom
            w: cmToInches(2.0),
            h: cmToInches(0.3),
          }
        });
      }

      // Bottom Right Copyright Text
      masterObjects.push({
        text: {
          text: `© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`,
          options: {
            x: cmToInches(10.0),
            y: cmToInches(13.39),
            w: cmToInches(14.5),
            h: cmToInches(0.6),
            fontSize: 6,
            color: '7F7F80',
            align: 'right',
            valign: 'bottom',
          }
        }
      });

      // Bottom Center Powered By Image
      if (masterImages.powerdbyImage && masterImages.powerdbyDimensions) {
        const desiredHeightCm = 0.35;
        const maxWidthCm = 3.0;
        const imageAspectRatio = masterImages.powerdbyDimensions.width / masterImages.powerdbyDimensions.height;
        let finalHeightCm = desiredHeightCm;
        let finalWidthCm = desiredHeightCm * imageAspectRatio;
        if (finalWidthCm > maxWidthCm) {
          finalWidthCm = maxWidthCm;
          finalHeightCm = maxWidthCm / imageAspectRatio;
        }
        const centerX = (slideWidthCm - finalWidthCm) / 2;
        masterObjects.push({
          image: {
            data: masterImages.powerdbyImage,
            x: cmToInches(centerX),
            y: cmToInches(13.39),
            w: cmToInches(finalWidthCm),
            h: cmToInches(finalHeightCm),
          }
        });
      }

      // Define master slide template
      pptx.defineSlideMaster({
        title: 'COLORTOKENS_MASTER',
        background: { color: 'FFFFFF' },
        objects: masterObjects,
        slideNumber: {
          x: cmToInches(24.0),
          y: cmToInches(13.39),
          w: cmToInches(1.0),
          h: cmToInches(0.6),
          color: '7F7F80',
          fontSize: 6,
          align: 'right',
          valign: 'bottom',
        },
      });

      // Create cover slide
      // Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm
      try {
        const coverSlideImage = await loadImageAsBase64(`${BLOB_STORAGE_BASE_URL}/coverslide.png`);
        const coverSlide = pptx.addSlide();
        coverSlide.background = { color: 'FFFFFF' };

        // Top Right Pattern Image (background)
        if (masterImages.topRightImage) {
          coverSlide.addImage({
            data: masterImages.topRightImage,
            x: cmToInches(17.78),
            y: 0.0,
            w: cmToInches(7.62),
            h: cmToInches(6.35),
          });
        }

        // Coverslide.png at Top Left
        if (coverSlideImage && coverSlideImage.dataUrl) {
          const imageAspectRatio = coverSlideImage.width / coverSlideImage.height;
          const maxWidthCm = 7.0;
          const maxHeightCm = 2.0;
          let finalWidthCm = maxWidthCm;
          let finalHeightCm = maxWidthCm / imageAspectRatio;
          if (finalHeightCm > maxHeightCm) {
            finalHeightCm = maxHeightCm;
            finalWidthCm = maxHeightCm * imageAspectRatio;
          }
          coverSlide.addImage({
            data: coverSlideImage.dataUrl,
            x: cmToInches(0.4),
            y: cmToInches(0.4),
            w: cmToInches(finalWidthCm),
            h: cmToInches(finalHeightCm),
          });
        }

        // Main Title
        coverSlide.addText(dashboardTitle || "Regional Sales Review Package", {
          x: cmToInches(0.4),
          y: cmToInches(5.5),
          w: cmToInches(24.6),
          h: cmToInches(1.2),
          fontSize: 32,
          fontFace: "Montserrat ExtraBold",
          align: "center",
          bold: true,
          color: "000000",
        });

        // Week and Quarter display
        const quarterDisplay = currentQuarter ? currentQuarter : 'Q1';
        const weekDisplay = currentWeekNumber != null ? currentWeekNumber : 0;
        const weekQuarterText = `Week ${weekDisplay}, ${quarterDisplay}`;
        coverSlide.addText(weekQuarterText, {
          x: cmToInches(0.4),
          y: cmToInches(7.5),
          w: cmToInches(24.6),
          h: cmToInches(0.6),
          fontSize: 18,
          fontFace: "Montserrat",
          align: "center",
          color: "000000",
        });

        // Week of Quarter subtext
        // const weekOfQuarterText = `(week ${weekDisplay} of ${quarterDisplay})`;
        // coverSlide.addText(weekOfQuarterText, {
        //   x: cmToInches(0.4),
        //   y: cmToInches(8.2),
        //   w: cmToInches(24.6),
        //   h: cmToInches(0.5),
        //   fontSize: 14,
        //   fontFace: "Montserrat",
        //   align: "center",
        //   color: "000000",
        // });

        // Bottom Left Pattern Image (background)
        if (masterImages.bottomLeftImage) {
          coverSlide.addImage({
            data: masterImages.bottomLeftImage,
            x: 0.0,
            y: cmToInches(9.50),
            w: cmToInches(6.35),
            h: cmToInches(4.826),
          });
        }

        coverSlide.addText(`© ${new Date().getFullYear()} ColorTokens, Inc. Confidential and Proprietary`, {
          x: cmToInches(10.0),
          y: cmToInches(13.39),
          w: cmToInches(14.5),
          h: cmToInches(0.6),
          fontSize: 6,
          color: '7F7F80',
          align: 'right',
          valign: 'bottom',
        });
      } catch (coverError) {
        console.error('Error creating cover slide:', coverError);
      }

      // Find all Paper elements that contain the main content sections
      const allPapers = Array.from(document.querySelectorAll('[class*="MuiPaper-root"]'));

      // Filter to get only top-level Paper elements (not nested within other Papers)
      const topLevelPapers = allPapers.filter(paper => {
        const parentPaper = paper.parentElement?.closest('[class*="MuiPaper-root"]');
        return !parentPaper || parentPaper === paper;
      });

      let sectionCount = 0;

      for (const paper of topLevelPapers) {
        // Skip if the paper is too small
        const rect = paper.getBoundingClientRect();
        if (rect.height < 150 || rect.width < 300) {
          continue;
        }

        // Skip if paper is not visible
        const computedStyle = window.getComputedStyle(paper);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
          continue;
        }

        // Skip filter containers
        if (paper.classList.contains('filterContainer') ||
            paper.closest('[class*="filterContainer"]')) {
          continue;
        }

        // Get slide title from the Paper
        const slideTitle = getSlideTitle(paper) || `Section ${sectionCount + 1}`;

        // Find all tables in this Paper
        const tables = Array.from(paper.querySelectorAll('table'));
        const visibleTables = tables.filter(table => table.offsetParent !== null);

        // Find all significant charts (SVG or canvas with minimum size)
        const allSvgs = Array.from(paper.querySelectorAll('svg'));
        const allCanvas = Array.from(paper.querySelectorAll('canvas'));

        // Filter to significant charts (exclude small icons/buttons)
        const significantSvgs = allSvgs.filter(svg => {
          const rect = svg.getBoundingClientRect();
          return rect.width > 100 && rect.height > 100;
        });
        const significantCanvas = allCanvas.filter(canvas => {
          const rect = canvas.getBoundingClientRect();
          return rect.width > 100 && rect.height > 100;
        });

        const hasSignificantCharts = significantSvgs.length > 0 || significantCanvas.length > 0;
        const hasOnlyTables = visibleTables.length > 0 && !hasSignificantCharts;
        const hasMixedContent = visibleTables.length > 0 && hasSignificantCharts;

        // Create first slide using master template
        const firstSlide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });

        // Decide how to export based on content
        if (hasOnlyTables) {
          // Export as native PowerPoint table (may create multiple slides)
          addTableToSlide(pptx, firstSlide, paper, slideTitle);
        } else if (hasMixedContent) {
          // Mixed content: export charts as image first, then tables separately
          // Temporarily hide tables while capturing chart image
          const tableElements = [];
          visibleTables.forEach(table => {
            const tableContainer = table.closest('[class*="MuiBox-root"]') || table.parentElement;
            if (tableContainer) {
              tableElements.push({
                element: tableContainer,
                originalDisplay: tableContainer.style.display
              });
              tableContainer.style.display = 'none';
            }
          });

          // Export charts as image
          await addChartImageToSlide(firstSlide, paper, slideTitle);

          // Restore tables
          tableElements.forEach(({ element, originalDisplay }) => {
            element.style.display = originalDisplay || '';
          });

          // Export each table as native PowerPoint table on separate slides
          for (const table of visibleTables) {
            // Find the wrapper div that contains both the title and the table
            // Structure: div.tableContainer > [Typography.title, TableContainer > Table]
            const tableContainer = table.closest('[class*="MuiTableContainer"]') || table.closest('[class*="MuiPaper-root"]');
            const wrapperDiv = tableContainer?.parentElement || table.parentElement;

            if (wrapperDiv) {
              // Get table-specific title
              let tableTitle = slideTitle;

              // Look for Typography h6 title within the wrapper div (sibling of TableContainer)
              const titleTypography = wrapperDiv.querySelector('[class*="MuiTypography-h6"]');
              if (titleTypography) {
                const subTitle = titleTypography.textContent.trim();
                if (subTitle && subTitle.length > 2 && subTitle.length < 100) {
                  tableTitle = subTitle;
                }
              } else {
                // Fallback: check preceding sibling
                const precedingSibling = (tableContainer || wrapperDiv).previousElementSibling;
                if (precedingSibling) {
                  const typographyEl = precedingSibling.querySelector('[class*="MuiTypography-"]') ||
                                       (precedingSibling.classList.contains('MuiTypography-root') ? precedingSibling : null);
                  if (typographyEl) {
                    const subTitle = typographyEl.textContent.trim();
                    if (subTitle && subTitle.length > 2 && subTitle.length < 100) {
                      tableTitle = subTitle;
                    }
                  }
                }
              }

              // Create a new slide for the table
              const tableSlide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
              addTableToSlide(pptx, tableSlide, wrapperDiv, tableTitle);
            }
          }
        } else {
          // Export as image (for charts only)
          await addChartImageToSlide(firstSlide, paper, slideTitle);
        }

        sectionCount++;

        // Update tooltip with progress
        setTooltipText(`Exporting section ${sectionCount}...`);
      }

      if (sectionCount === 0) {
        throw new Error('No content sections found to export');
      }

      // Generate filename with current quarter and week if available
      let finalFileName = fileName;
      if (currentQuarter && currentWeekNumber) {
        finalFileName = `${fileName}_${currentQuarter}_Week${currentWeekNumber}`;
      }

      // Save the PowerPoint presentation with date and time
      // Generate date and time string: YYYY-MM-DD_HH-mm-ss
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const dateTimeString = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;

      await pptx.writeFile(`${finalFileName}-${dateTimeString}.pptx`);

      setLoading(false);
      setTooltipText("PowerPoint Downloaded");

      // Reset tooltip after 5 seconds
      setTimeout(() => {
        setTooltipText("Download as PowerPoint");
      }, 5000);
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      setLoading(false);
      setTooltipText("Export failed. Please try again.");
      setTimeout(() => {
        setTooltipText("Download as PowerPoint");
      }, 5000);
    }
  };

  // Inline icon button style (like ExportPPT.js)
  if (inline) {
    const iconStyles = {
      width: "20px",
      height: "20px",
      marginRight: "3px",
      color: "transparent",
      fill: "#A9A9A9",
    };

    return (
      <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
        <CustomWidthTooltip title={tooltipText} placement="left">
          <button
            onClick={handleExport}
            disabled={loading}
            style={{
              border: "none",
              background: "transparent",
              cursor: loading ? "default" : "pointer",
              position: "relative",
              opacity: loading ? 0.5 : 1,
            }}
          >
            <GetAppIcon style={{ color: "#808080", ...iconStyles }} />

            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 2,
                }}
              >
                <img
                  src="/test_loading.gif"
                  alt="Loading..."
                  style={{
                    width: "20px",
                    height: "20px",
                  }}
                />
              </div>
            )}
          </button>
        </CustomWidthTooltip>
      </div>
    );
  }

  // Fixed button style (default)
  return (
    <CustomWidthTooltip title={tooltipText} placement="left">
      <Button
        onClick={handleExport}
        disabled={loading}
        variant="contained"
        color="primary"
        startIcon={loading ? null : <GetAppIcon />}
        style={{
          margin: "1rem",
          position: "fixed",
          top: "80px",
          right: "20px",
          zIndex: 1000,
          minWidth: "120px",
        }}
      >
        {loading ? "Exporting..." : "Export PPTX"}
      </Button>
    </CustomWidthTooltip>
  );
}

export default RegionalSalesReviewPackagePPTX;
